'use client';

import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { ChevronDown, Clock, Download, BookOpen, Bookmark, X } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { scrollToElement } from './utils/scroll-positioning';
import { emergencyLaunchConfig } from '../../../config/emergency-launch';
import { ButtonCta } from '@/components/ui/ButtonCta';

// Dynamic import for PDF button to avoid SSR issues
const PDFDownloadButton = dynamic(() => import('../pdf/PDFDownloadButton'), {
  ssr: false,
  loading: () => (
    <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-md border border-blue-200 opacity-50 cursor-not-allowed" disabled>
      <Download className="w-4 h-4" />
      <span className="text-sm font-medium">Loading PDF...</span>
    </button>
  ),
});

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface GuideSidebarProps {
  headings: Heading[];
  scrollProgress: number;
  estimatedReadingTime: number;
  currentSection: string;
  guideUrl: string;
  hasBookmark?: boolean;
  isSaving?: boolean;
  onItemClick?: (targetId?: string) => void;
  guideTitle?: string;
  guideSubtitle?: string;
  guideAuthor?: string;
  guideContent?: any;
  rawContent?: string;
}

export function GuideSidebar({ 
  headings, 
  scrollProgress, 
  estimatedReadingTime,
  currentSection,
  guideUrl,
  hasBookmark: hasBookmarkProp = false,
  isSaving: isSavingProp = false,
  onItemClick,
  guideTitle,
  guideSubtitle,
  guideAuthor,
  guideContent,
  rawContent
}: GuideSidebarProps) {
  const [activeId, setActiveId] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [timeRemaining, setTimeRemaining] = useState(estimatedReadingTime);
  const [dotCount, setDotCount] = useState(1);
  const [tocMaxHeight, setTocMaxHeight] = useState('calc(100vh - 24rem)');
  const [sidebarMaxHeight, setSidebarMaxHeight] = useState('auto');
  
  // Use props for bookmark state
  const hasBookmark = hasBookmarkProp;
  const isSaving = isSavingProp;
  
  // Bookmark storage key
  const storageKey = `guideProgress:${guideUrl}`;
  
  // Calculate time remaining based on scroll progress
  useEffect(() => {
    const remaining = Math.ceil(estimatedReadingTime * (1 - scrollProgress / 100));
    setTimeRemaining(Math.max(0, remaining));
  }, [scrollProgress, estimatedReadingTime]);
  
  // Note: Bookmark checking and saving is now handled by GuidePageTemplate parent component
  
  // Animate dots when saving
  useEffect(() => {
    if (isSaving) {
      const interval = setInterval(() => {
        setDotCount(prev => (prev % 3) + 1);
      }, 333);
      
      return () => clearInterval(interval);
    }
  }, [isSaving]);
  
  // Dynamically calculate available height for TOC and sidebar
  useEffect(() => {
    const calculateAvailableHeight = () => {
      // Check if we're in mobile modal (onItemClick is only passed on mobile)
      if (onItemClick) {
        // For mobile: use full height available in the modal
        setSidebarMaxHeight('100%');
        // Mobile TOC gets much more space - just account for header content above it
        setTocMaxHeight('calc(100vh - 20rem)'); // Generous height for mobile
        return;
      }
      
      // Desktop calculations (unchanged)
      // Get the sidebar header section height (everything above TOC)
      const sidebarHeader = document.querySelector('.guide-sidebar-header');
      const headerHeight = sidebarHeader?.getBoundingClientRect().height || 320;
      
      // Get the sidebar footer height
      const sidebarFooter = document.querySelector('.guide-sidebar-footer');
      const footerHeight = sidebarFooter?.getBoundingClientRect().height || 60;
      
      // Check if bottom navigation exists and get its height
      const bottomNav = document.querySelector('.section-navigation-bottom');
      const actualBottomNavHeight = bottomNav?.getBoundingClientRect().height;
      // If bottom nav exists, use its actual height, otherwise assume it will be ~69px when it appears
      const bottomNavHeight = actualBottomNavHeight || 69;
      
      // The sidebar is positioned with sticky top-32 (128px from viewport top)
      const sidebarTopOffset = 128;
      
      // We want 20px gap but were getting 55px, so we need to ADD 35px to close the gap
      const gapCorrection = 35;
      
      // Calculate total sidebar height to fit above bottom nav
      const totalSidebarHeight = window.innerHeight - sidebarTopOffset - bottomNavHeight - 20 + gapCorrection;
      
      setSidebarMaxHeight(`${Math.max(400, totalSidebarHeight)}px`);
      
      // Calculate available height for TOC scrollable area
      const tocAvailableHeight = totalSidebarHeight - headerHeight - footerHeight - 32; // 32 for padding
      setTocMaxHeight(`${Math.max(200, tocAvailableHeight)}px`);
    };
    
    // Initial calculation
    calculateAvailableHeight();
    
    // Recalculate on resize
    window.addEventListener('resize', calculateAvailableHeight);
    
    // Recalculate after brief delays to ensure DOM is ready
    const timer1 = setTimeout(calculateAvailableHeight, 100);
    const timer2 = setTimeout(calculateAvailableHeight, 500);
    
    return () => {
      window.removeEventListener('resize', calculateAvailableHeight);
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [onItemClick]);
  
  // Track active heading with Intersection Observer
  useEffect(() => {
    // Track all currently intersecting headings
    const intersectingHeadingsMap = new Map<string, IntersectionObserverEntry>();
    
    // Flag to block observer updates during click-initiated scrolls
    let isUserScrolling = false;
    let userScrollTimeout: NodeJS.Timeout;
    
    // Debounce function for observer callback
    let timeoutId: NodeJS.Timeout;
    const debounce = (func: Function, wait: number) => {
      return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), wait);
      };
    };
    
    // Function to determine which heading should be active
    const updateActiveHeading = () => {
      // Don't update if user just clicked - respect their explicit choice
      if (isUserScrolling) return;
      
      // Get all currently intersecting headings with FRESH position data
      const currentlyIntersecting = Array.from(intersectingHeadingsMap.values())
        .filter(entry => entry.isIntersecting)
        .map(entry => {
          // Get FRESH position instead of using stale boundingClientRect
          const element = entry.target as HTMLElement;
          const freshRect = element.getBoundingClientRect();
          return {
            id: entry.target.id,
            level: parseInt(entry.target.tagName.charAt(1)),
            top: freshRect.top  // Use fresh position, not stale entry.boundingClientRect
          };
        });
      
      if (currentlyIntersecting.length === 0) return;
      
      // Sort by: 1) Distance from reading position (20% from top), 2) Heading level (H2 > H3)
      currentlyIntersecting.sort((a, b) => {
        // Calculate distance from ideal reading position (20% from viewport top)
        const readingPosition = window.innerHeight * 0.2;
        const aDist = Math.abs(a.top - readingPosition);
        const bDist = Math.abs(b.top - readingPosition);
        
        // If headings are within 50px of each other, prioritize by level
        if (Math.abs(aDist - bDist) < 50) {
          return a.level - b.level; // H2 (level 2) comes before H3 (level 3)
        }
        
        // Otherwise, closest to reading position wins
        return aDist - bDist;
      });
      
      // Set the most appropriate heading as active
      setActiveId(currentlyIntersecting[0].id);
    };
    
    // Create debounced version of updateActiveHeading
    const debouncedUpdateActiveHeading = debounce(updateActiveHeading, 50);
    
    // Smart observer callback that maintains the full state
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // IMMEDIATELY update our map with the latest state for changed entries
      entries.forEach(entry => {
        intersectingHeadingsMap.set(entry.target.id, entry);
      });
      
      // Debounce the decision about which heading should be active
      debouncedUpdateActiveHeading();
    };
    
    // Listen for our custom event that signals a user click
    const handleUserClick = () => {
      isUserScrolling = true;
      clearTimeout(userScrollTimeout);
      // Block observer updates for 600ms (smooth scroll duration)
      userScrollTimeout = setTimeout(() => {
        isUserScrolling = false;
      }, 600);
    };
    
    window.addEventListener('toc-click', handleUserClick);
    
    const observer = new IntersectionObserver(
      observerCallback,  // NOT debounced - we need immediate state updates
      { rootMargin: '-20% 0% -70% 0%' }
    );
    
    const contentElement = document.querySelector('.guide-content');
    if (contentElement) {
      const headingElements = contentElement.querySelectorAll('h2, h3');
      headingElements.forEach((heading) => observer.observe(heading));
      
      return () => {
        clearTimeout(timeoutId);
        clearTimeout(userScrollTimeout);
        window.removeEventListener('toc-click', handleUserClick);
        headingElements.forEach((heading) => observer.unobserve(heading));
      };
    }
  }, [headings]);
  
  // Group headings by H2 sections
  const sections = headings.reduce((acc, heading) => {
    if (heading.level === 2) {
      acc.push({ parent: heading, children: [] });
    } else if (heading.level === 3 && acc.length > 0) {
      acc[acc.length - 1].children.push(heading);
    }
    return acc;
  }, [] as Array<{ parent: Heading; children: Heading[] }>);
  
  const scrollToHeading = async (id: string) => {
    // Set active state immediately for visual feedback
    setActiveId(id);
    
    // CRITICAL: Mark that we're navigating from TOC to prevent auto-restore conflicts
    sessionStorage.setItem('fromTOC', 'true');
    
    // Check if we're on mobile (onItemClick is only passed on mobile)
    if (onItemClick) {
      // Mobile: Pass the target ID to parent for deferred scrolling
      onItemClick(id);
    } else {
      // Desktop: Use bulletproof scroll positioning
      try {
        const result = await scrollToElement({
          targetElementId: id,
          forceScrollBehavior: 'smooth',
          debugMode: false, // Set to true for debugging
        });
        
        if (!result.success) {
          console.warn(`[GuideSidebar] Failed to scroll to heading: ${id}`);
        }
      } catch (error) {
        console.error(`[GuideSidebar] Error scrolling to heading ${id}:`, error);
        // Fallback to old method
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };
  
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };
  
  const clearBookmark = () => {
    localStorage.removeItem(storageKey);
    // Force page refresh to reset bookmark state in parent
    window.location.reload();
  };
  
  const restoreBookmark = () => {
    const savedPosition = localStorage.getItem(storageKey);
    if (savedPosition) {
      const position = parseInt(savedPosition);
      window.scrollTo({
        top: position,
        behavior: 'smooth'
      });
    }
  };
  
  // Bookmark restore handlers removed - now handled by parent GuidePageTemplate
  
  return (
    <div className={cn(
      "guide-sidebar-container bg-white shadow-sm",
      onItemClick ? "flex flex-col h-full" : "rounded-lg border border-gray-200"
    )} style={{ maxHeight: onItemClick ? '100%' : sidebarMaxHeight, overflow: 'hidden' }}>
      {/* Header */}
      <div className="guide-sidebar-header p-4 border-b border-gray-200">
        <p className="font-semibold text-gray-900 mb-3">Guide Contents</p>
        
        {/* Reading Stats */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="flex items-center text-gray-600">
              <Clock className="w-4 h-4 mr-2" />
              Time remaining
            </span>
            <span className="font-medium text-gray-900">
              {timeRemaining} min
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="flex items-center text-gray-600">
              <BookOpen className="w-4 h-4 mr-2" />
              Progress
            </span>
            <span className="font-medium text-gray-900">
              {Math.round(scrollProgress)}%
            </span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#294bfd] to-[#ff34a7] transition-all duration-300"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>
        
        {/* Download Button - Feature Flagged */}
        {emergencyLaunchConfig.features.showPDFDownload && (
          <div className="mt-4">
            {guideTitle && (guideContent || rawContent) ? (
              <PDFDownloadButton
                title={guideTitle}
                subtitle={guideSubtitle}
                author={guideAuthor}
                content={guideContent}
                rawContent={rawContent}
                guideUrl={guideUrl}
              />
            ) : (
              <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-md border border-blue-200 opacity-50 cursor-not-allowed" disabled>
                <Download className="w-4 h-4" />
                <span className="text-sm font-medium">Download PDF</span>
              </button>
            )}
          </div>
        )}
        
        {/* Bookmark Controls */}
        {hasBookmark ? (
          <div className="mt-3 p-2 bg-gray-50 rounded-md border border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={restoreBookmark}
                className={`flex items-center space-x-1.5 transition-colors ${
                  isSaving 
                    ? 'text-blue-700 hover:text-blue-900' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Bookmark className="w-3.5 h-3.5 fill-current" />
                <span className="text-xs font-medium">
                  {isSaving ? `Position Saving${'.'.repeat(dotCount)}` : 'Position Saved'}
                </span>
              </button>
              <button
                onClick={clearBookmark}
                className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                aria-label="Clear saved position"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3 text-xs text-gray-500 text-center">
            Auto-saving reading position...
          </div>
        )}
      </div>
      
      {/* Table of Contents */}
      <div className={cn(
        "p-4 overflow-y-auto",
        onItemClick && "flex-1"
      )} style={{ maxHeight: onItemClick ? 'auto' : tocMaxHeight }}>
        <nav className="space-y-1">
          {sections.map((section) => {
            const isExpanded = expandedSections.has(section.parent.id) || section.children.length === 0;
            const isActive = activeId === section.parent.id || section.children.some(child => child.id === activeId);
            
            return (
              <div key={section.parent.id} className="space-y-1">
                <div
                  className={cn(
                    "w-full flex items-center justify-between text-left px-3 py-1.5 rounded-md transition-all",
                    isActive && "border-l-4 border-primary bg-primary/5",
                    !isActive && "hover:bg-gray-100",
                    "group"
                  )}
                >
                  <button
                    onClick={() => {
                      scrollToHeading(section.parent.id);
                      // Auto-expand when navigating to a section with children for better UX
                      if (section.children.length > 0 && !expandedSections.has(section.parent.id)) {
                        toggleSection(section.parent.id);
                      }
                    }}
                    className="flex-1 text-left"
                  >
                    <span className={cn(
                      "text-sm",
                      isActive ? "text-primary font-semibold" : "text-gray-700 font-medium"
                    )}>
                      {section.parent.text}
                    </span>
                  </button>
                  {section.children.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection(section.parent.id);
                      }}
                      className="p-1 -mr-1 hover:bg-gray-200 rounded transition-colors"
                      aria-label={isExpanded ? "Collapse section" : "Expand section"}
                    >
                      <ChevronDown 
                        className={cn(
                          "text-gray-400 transition-transform flex-shrink-0",
                          isExpanded && "rotate-180"
                        )}
                        style={{ width: '16px', height: '16px' }}
                      />
                    </button>
                  )}
                </div>
                
                {/* H3 Children */}
                {isExpanded && section.children.length > 0 && (
                  <div className="ml-3 bg-gray-50 rounded-md p-1.5 space-y-0.5">
                    {section.children.map((child) => {
                      const isChildActive = activeId === child.id;
                      
                      return (
                        <button
                          key={child.id}
                          onClick={() => scrollToHeading(child.id)}
                          className={cn(
                            "block w-full text-left text-sm px-2 py-1.5 rounded transition-colors",
                            isChildActive 
                              ? "text-primary font-medium bg-white" 
                              : "text-gray-600 hover:text-primary hover:bg-white"
                          )}
                        >
                          {child.text}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
      
      {/* Footer */}
      <div className={cn(
        "guide-sidebar-footer p-3 border-t border-gray-200",
        onItemClick && "flex-shrink-0"
      )}>
        <ButtonCta
          href="/mortgage-lead-generation-demo"
          variant="primary"
          showArrow={true}
          external={true}
          className="w-full text-sm"
        >
          Book Strategy Call
        </ButtonCta>
      </div>
      
      {/* Toast notification moved to parent GuidePageTemplate for proper z-index layering */}
    </div>
  );
}